import { css, cx } from '@linaria/core'
import { type FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { EnhancedProductVariantId } from '#api/enhancements/enhancedTmsApiTypes'
import { useAppDispatch, useAppSelector } from '#app/store'
import {
  clearViewHighlight,
  type LocalConfigurationId,
  setVariantForNationalCodeFamily,
  setVariantsForFamily,
} from '#app/store/slices/configurationSlice'
import { Gallery } from '#components/Gallery/Gallery'
import { type MultiOverlayOption, TypedMultiOverlaySelector } from '#components/MultiOverlaySelector'
import { CosimaViewComponent } from '#features/Cosima/Cosima'
import type { FigureComponent, MetaFigureComponent } from '#features/Cosima/document'
import type { EnhancedCosimaRoot } from '#features/Cosima/util'
import { cosimaDocumentClass } from '#pages/Bridge/style'
import { fragments } from '#styles'
import { useConfigurationEnrichment } from '../hooks/useActiveConfigurationEnrichment'
import { isLocalConfigurationId } from './useActiveConfiguration'
import { useConfigurationContext } from './useConfigurationContext'

const overlayFamilyIdClass = css`
  ${fragments.textStyle.heading.xl.subtle}
`

const overlayFamilyTitleClass = css`
  padding-left: 5px;
  ${fragments.textStyle.heading.xl.regular}
`

const overlayClass = css`
  flex: 1 1 0;
  min-width: 50vw;

  > div {
    min-width: 100%;
    width: 1600px;
    max-width: 1600px;
    min-height: 50vh;

    main > div > ._short-description {
      margin-bottom: 20px;
      ${fragments.textStyle.body.m.regular};
      white-space: preserve;
    }
  }
`

const cosimaDetailsClass = css`
  flex: 1;
  > ._title {
    display: flex;
    gap: 12px;
    padding-bottom: 12px;

    > ._code {
      ${fragments.textStyle.heading.xl.subtle};
    }

    > ._name {
      ${fragments.textStyle.heading.xl.regular}
    }
  }

  h1 {
    padding-top: 24px;
    ${fragments.textStyle.heading.l.regular};
  }

  img {
    max-width: 100%;
  }
`

const CosimaDetails: FC<{ root: EnhancedCosimaRoot | undefined; code: string; name: string }> = ({ root, code, name }) => {
  return (
    <div className={cx(cosimaDocumentClass, cosimaDetailsClass)}>
      <h1 className="_title">
        <span className="_code">{code}</span>
        <span className="_name">{name}</span>
      </h1>

      <CosimaViewComponent details={root?.details.shortDescription} />
      <CosimaViewComponent details={root?.details.benefits} />
      <CosimaViewComponent details={root?.details.description} />
      <CosimaViewComponent details={root?.details.techdata} />
    </div>
  )
}

const CosimaGallery: FC<{ figure: (FigureComponent | MetaFigureComponent | undefined)[] }> = ({ figure }) => {
  const images = useMemo(() => figure.filter((x) => !!x).flatMap((x) => x.images), [figure])
  if (images.length <= 0) {
    return undefined
  }
  return (
    <Gallery
      items={images.flatMap((x, i) => ({ type: 'image' as const, id: `${i}-${x.fileName}`, url: `/cosima/${x.fileName}`, alt: x.name }))}
      canFullscreen
    />
  )
}

const MultiOverlaySelector = TypedMultiOverlaySelector<MultiOverlayOption<EnhancedProductVariantId>>()

const useOptionOverlayConfiguration = (configurationId: LocalConfigurationId | undefined) => {
  const { configurationId: activeConfigurationId } = useConfigurationContext()
  const activeLocalConfigurationId = isLocalConfigurationId(activeConfigurationId) ? activeConfigurationId : undefined
  const usedConfigurationId = configurationId ?? activeLocalConfigurationId
  const configurations = useAppSelector((state) => state.truckConfigurations.configurations)
  const configuration = usedConfigurationId ? configurations[usedConfigurationId] : undefined

  return useConfigurationEnrichment(configuration)
}

export const OptionOverlay: FC<{ configurationId?: LocalConfigurationId }> = ({ configurationId }) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const { configuration, enrichment } = useOptionOverlayConfiguration(configurationId)

  const highlight = configuration?.viewHighlight.modal

  if (!configuration || !highlight || !enrichment) {
    return undefined
  }

  const highlightFamily = enrichment.enrichedFamilies[highlight.familyId]

  const selectedVariant = highlight.variantId
    ? enrichment.enrichedVariants[`${highlight.familyId}${highlight.variantId}`]
    : highlightFamily?.selectedVariant

  if (!highlightFamily) {
    return undefined
  }

  const variants =
    highlight.displayMode === 'read-only' ? highlightFamily.variants.filter((v) => v.id === highlight.variantId) : highlightFamily.variants

  const options = variants.map((variant): MultiOverlayOption<EnhancedProductVariantId> => {
    const cosimaDocument = variant.cosima
    if (cosimaDocument) {
      return {
        loading: false,
        id: variant.id,
        source: variant.source,
        displayName: `${variant.title} (${variant.id})`,
        primaryContent: <CosimaDetails root={cosimaDocument} code={`${highlightFamily.id}${variant.id}`} name={variant.title} />,
        asideContent: <CosimaGallery figure={[cosimaDocument?.details.meta?.figure, ...(cosimaDocument?.details.techdata?.figure ?? [])]} />,
      }
    }

    const familyDetails = highlightFamily.detailData
    const variantDetails = variant.detailData
    if (!familyDetails && !variantDetails) {
      return {
        loading: false,
        id: variant.id,
        source: variant.source,
        displayName: `${variant.title} (${variant.id})`,
        primaryContent: undefined,
        asideContent: undefined,
      }
    }

    return {
      loading: false,
      id: variant.id,
      source: variant.source,
      displayName: `${variant.title} (${variant.id})`,
      primaryContent: (
        <div>
          <h1>
            {variant.title} ({variant.id})
          </h1>
          {familyDetails?.description && <section className="_short-description">{familyDetails.description}</section>}
          {variantDetails?.description ? (
            <section className="_short-description">{variantDetails.description}</section>
          ) : (
            t('configuration.detail.no-variant-description', 'No variant description available')
          )}
        </div>
      ),
      asideContent: undefined,
    }
  })

  return (
    <MultiOverlaySelector
      open={!!highlight}
      title={
        <>
          <span className={overlayFamilyIdClass}>{highlightFamily.id}</span>
          <span className={overlayFamilyTitleClass}>{highlightFamily.title}</span>
        </>
      }
      initialSelectionId={highlightFamily.selectedVariant?.id}
      selectedOptionId={selectedVariant?.id}
      options={options}
      onClosing={(option) => {
        dispatch(
          clearViewHighlight({
            id: configuration.id,
            scope: 'modal',
          }),
        )

        if (option && option.id !== selectedVariant?.id && highlight.displayMode !== 'read-only') {
          if (option.source === 'ecm') {
            dispatch(
              setVariantsForFamily({
                configurationId: configuration.id,
                selections: [
                  {
                    familyId: highlightFamily.id,
                    toVariantId: option.id,
                  },
                ],
              }),
            )
          } else if (option.source === 'national-code') {
            dispatch(
              setVariantForNationalCodeFamily({
                configurationId: configuration.id,
                familyId: highlightFamily.id,
                toVariantId: option.id,
              }),
            )
          }
        }
      }}
      className={overlayClass}
    />
  )
}
